import { useCallback, useState } from 'react'
import req from '@sportninja/common/api/request'

interface AcceptPayload {
  edited_parameters?: Record<string, unknown>
  edited_division_uids?: string[] | null
  edited_team_uids?: string[] | null
  edited_is_hard?: boolean
}

interface BulkAcceptItem extends AcceptPayload {
  suggestion_uid: string
}

interface UseAiSuggestActionsOptions {
  flightId: string | null
  /** Called after a successful accept (single or bulk) so the constraints table can refresh. */
  onAccepted?: () => void | Promise<void>
}

interface UseAiSuggestActionsResult {
  acceptOne: (suggestionId: string, edits?: AcceptPayload) => Promise<void>
  rejectOne: (suggestionId: string) => Promise<void>
  acceptMany: (items: BulkAcceptItem[]) => Promise<void>
  isMutating: boolean
  error: Error | null
}

/**
 * Hook that exposes the accept / reject / bulk-accept actions on AI
 * suggestions. After every successful accept (single or bulk) it calls
 * the optional `onAccepted` callback so the caller can refresh the
 * constraints table to show the newly-created rows.
 */
export const useAiSuggestActions = ({
  flightId,
  onAccepted,
}: UseAiSuggestActionsOptions): UseAiSuggestActionsResult => {
  'use memo'

  const [isMutating, setIsMutating] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const acceptOne = useCallback(
    async (suggestionId: string, edits: AcceptPayload = {}): Promise<void> => {
      if (!flightId) {
        return
      }
      setIsMutating(true)
      setError(null)
      try {
        await req(
          `/scheduler/flights/${flightId}/ai-suggestions/${suggestionId}/accept`,
          {
            method: 'POST',
            body: JSON.stringify(edits),
          }
        )
        if (onAccepted) {
          await onAccepted()
        }
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err : new Error('Failed to accept suggestion')
        )
        throw err
      } finally {
        setIsMutating(false)
      }
    },
    [flightId, onAccepted]
  )

  const rejectOne = useCallback(
    async (suggestionId: string): Promise<void> => {
      if (!flightId) {
        return
      }
      setIsMutating(true)
      setError(null)
      try {
        await req(
          `/scheduler/flights/${flightId}/ai-suggestions/${suggestionId}/reject`,
          { method: 'POST' }
        )
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err : new Error('Failed to reject suggestion')
        )
        throw err
      } finally {
        setIsMutating(false)
      }
    },
    [flightId]
  )

  const acceptMany = useCallback(
    async (items: BulkAcceptItem[]): Promise<void> => {
      if (!flightId || items.length === 0) {
        return
      }
      setIsMutating(true)
      setError(null)
      try {
        await req(`/scheduler/flights/${flightId}/ai-suggestions/bulk-accept`, {
          method: 'POST',
          body: JSON.stringify({ acceptances: items }),
        })
        if (onAccepted) {
          await onAccepted()
        }
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? err
            : new Error('Failed to bulk-accept suggestions')
        )
        throw err
      } finally {
        setIsMutating(false)
      }
    },
    [flightId, onAccepted]
  )

  return { acceptOne, rejectOne, acceptMany, isMutating, error }
}
